import { Box, FormControl, LinearProgress, TextField } from '@mui/material';
import { Form, useForm } from '@redwoodjs/forms';
import { useCallback, useState } from 'react';
import { useCreateNote } from 'src/api/notes.api';
import { AgButton, AgModal, AgModalActions, AgModalContent, AgText } from 'src/components/ui';
import type { NewNote } from '../NewComment';
import { useRefetchNoteHosts } from '../useRefetchNoteHosts';

type NoteRequiredModal = {
  handleClose: () => void;
  headerLabel: string;
  contentText: string;
  confirmLabel: string;

  additionalOptions?: React.ReactNode;

  // List out all actions that will be taken after action. (Shown as a description for user to confirm)
  postActionNotes?: string[];

  onSubmit: (comment?: string) => Promise<unknown>;

  // enabled by default - the modal itself will handle posting the comment
  autoHandleCommentPost?: boolean;
  open: boolean;

  /** Test ID for the confirm/submit button */
  confirmTestId?: string;
  /** Test ID for the note textarea input */
  noteInputTestId?: string;
  postCommentTo?: NewNote['postCommentTo'];
} & Omit<NewNote, 'onCreate' | 'postCommentTo'>;

export default function NoteRequiredModal({
  handleClose,
  onSubmit,
  headerLabel,
  contentText,
  confirmLabel,
  postActionNotes,
  open,
  external,
  postCommentTo,
  additionalOptions = null,
  autoHandleCommentPost = true,
  confirmTestId,
  noteInputTestId,
}: NoteRequiredModal) {
  const [submitting, setSubmitting] = useState(false);

  const { create: createComment } = useCreateNote();
  const refetchNoteHosts = useRefetchNoteHosts();

  const formMethods = useForm<{ comment: string }>({ defaultValues: { comment: '' } });

  const { register, reset } = formMethods;

  const handleSubmit = useCallback(
    ({ comment }: { comment: string }) => {
      setSubmitting(true);
      // Only post a comment when non-blank — confirming with empty text would create a blank comment.
      if (autoHandleCommentPost && comment?.trim()) {
        createComment({ ...postCommentTo, input: { text: comment, external } }).then(() => {
          void refetchNoteHosts(postCommentTo);

          onSubmit(comment)
            .then(() => {
              reset();
              handleClose();
            })
            .finally(() => {
              setSubmitting(false);
            });
        });
      } else {
        onSubmit(comment)
          .then(() => {
            // With autoHandleCommentPost off, onSubmit's own mutation carried the note (SO/PO flag).
            if (!autoHandleCommentPost && comment?.trim()) void refetchNoteHosts(postCommentTo);
            reset();
            handleClose();
          })
          .finally(() => {
            setSubmitting(false);
          });
      }
    },
    [postCommentTo, external, onSubmit, handleClose, createComment, reset, refetchNoteHosts, autoHandleCommentPost]
  );

  return (
    <AgModal open={open} onClose={handleClose} title={headerLabel}>
      <Form onSubmit={handleSubmit} formMethods={formMethods}>
        <AgModalContent>
          <p className="text-sm text-neutral-secondary">{contentText}</p>
          <FormControl sx={{ width: '100%', pt: 3 }}>
            <TextField
              multiline
              disabled={submitting}
              minRows={3}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              defaultValue=""
              slotProps={noteInputTestId ? { htmlInput: { 'data-testid': noteInputTestId } } : undefined}
              {...register('comment')}
            />

            {additionalOptions}

            {postActionNotes && postActionNotes.length > 0 && (
              <Box>
                <AgText size="16" color="secondary" className="mt-2">
                  The following actions will be taken:
                </AgText>
                <Box sx={{ ml: 2.5 }}>
                  {postActionNotes.map((note) => (
                    <p key={note} className="mt-1 text-sm text-neutral-secondary">
                      <li>{note}</li>
                    </p>
                  ))}
                </Box>
              </Box>
            )}
          </FormControl>
        </AgModalContent>
        <AgModalActions onCancel={handleClose}>
          <Box sx={{ position: 'relative' }}>
            <AgButton type="submit" variant="primary" isDisabled={submitting} data-testid={confirmTestId}>
              {confirmLabel}
            </AgButton>
            <LinearProgress
              sx={{
                height: submitting ? 3 : 0,
                transition: 'height 0.3s ease-in-out',
                position: 'absolute',
                transform: 'translate(0, -100%)',
                width: '100%',
                borderRadius: '0 0 4px 4px',
              }}
            />
          </Box>
        </AgModalActions>
      </Form>
    </AgModal>
  );
}
